import React, { useState, useRef, useEffect } from 'react';
import { Send, X, Edit, AlertTriangle } from 'lucide-react';
import Button from '../../../../components/common/Button/Button';
import { handleTextInputChange, getCharacterCount, FIELD_LIMITS } from '../../../../utils/inputValidation';

const SendQuoteModal = ({
    isOpen,
    onClose,
    quote,
    defaultEmail,
    onSend,
    isSending
}) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const [isEmailEditable, setIsEmailEditable] = useState(false);
    const [sendForm, setSendForm] = useState({
        email: '',
        message: ''
    });

    useEffect(() => {
        if (isOpen) {
            // Reset form on open
            setSendForm({
                email: defaultEmail || '',
                message: ''
            });
            setIsEmailEditable(false);
            setShowConfirm(false);
        }
    }, [isOpen, defaultEmail]);

    if (!isOpen) return null;

    const isResend = quote?.status?.toLowerCase() === 'sent' || quote?.sentDate;

    const handleConfirmClick = () => {
        if (!sendForm.email || !sendForm.email.trim()) {
            alert('Please enter recipient email address');
            return;
        }
        setShowConfirm(true);
    };

    const handleExecuteSend = () => {
        onSend({
            email: sendForm.email,
            message: sendForm.message,
            isResend
        });
    };

    if (showConfirm) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                    <div className="flex items-center justify-center mb-4 text-amber-500">
                        <AlertTriangle className="h-12 w-12" />
                    </div>
                    <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                        Confirm {isResend ? 'Resend' : 'Send'}
                    </h3>
                    <div className="text-center mb-6">
                        <p className="text-gray-500 mb-2">
                            The quotation will be sent to <strong>{sendForm.email}</strong>
                        </p>
                        <p className="text-sm text-gray-700">
                            {isResend
                                ? 'Are you sure you want to resend this quotation?'
                                : 'Are you sure you want to send this quotation?'}
                        </p>
                    </div>
                    <div className="flex items-center justify-end space-x-3">
                        <Button
                            variant="outline"
                            onClick={() => setShowConfirm(false)}
                            disabled={isSending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleExecuteSend}
                            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                            icon={<Send className="h-4 w-4" />}
                            disabled={isSending}
                        >
                            {isSending ? 'Sending...' : (isResend ? 'Resend' : 'Send')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isResend ? 'Resend Quote' : 'Send Quote'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Recipient Email <span className="text-red-500">*</span>
                        </label>
                        <div className="flex space-x-2">
                            <input
                                type="email"
                                value={sendForm.email}
                                onChange={(e) => setSendForm({ ...sendForm, email: e.target.value })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${!isEmailEditable ? 'bg-gray-100 text-gray-500' : 'border-gray-300'
                                    }`}
                                placeholder="recipient@example.com"
                                required
                                disabled={!isEmailEditable}
                            />
                            {!isEmailEditable ? (
                                <button
                                    type="button"
                                    onClick={() => setIsEmailEditable(true)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    title="Edit Email"
                                >
                                    <Edit className="h-4 w-4" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setIsEmailEditable(false)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    title="Cancel Edit"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Additional Message (Optional)
                        </label>
                        <div className="space-y-1">
                            <textarea
                                rows={5}
                                value={sendForm.message}
                                onChange={(e) => {
                                    const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.message);
                                    setSendForm({ ...sendForm, message: sanitized });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                placeholder="Add any additional message for the resend..."
                                maxLength={FIELD_LIMITS.message}
                            />
                            <div className="text-right text-xs text-gray-500">
                                {getCharacterCount(sendForm.message, FIELD_LIMITS.message)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        icon={<Send className="h-4 w-4" />}
                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                        onClick={handleConfirmClick}
                        disabled={isSending || !sendForm.email || !sendForm.email.trim()}
                    >
                        {isSending ? 'Sending...' : (isResend ? 'Resend Quote' : 'Send Quote')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SendQuoteModal;
